import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { API } from '../App';

/** Tiny markdown renderer — h1/h2/h3, bold, italic, list, paragraph. */
function renderMarkdown(md) {
  const lines = (md || '').split(/\r?\n/);
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(<ul key={`l${out.length}`} className="list-disc pl-6 my-4 space-y-1">{listBuf}</ul>);
      listBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flushList(); return; }
    if (line.startsWith('# ')) { flushList(); out.push(<h1 key={`h${i}`} className="text-3xl font-heading font-medium mt-8 mb-4" dangerouslySetInnerHTML={{__html: inline(line.slice(2))}} />); return; }
    if (line.startsWith('## ')) { flushList(); out.push(<h2 key={`h${i}`} className="text-2xl font-heading font-medium mt-6 mb-3" dangerouslySetInnerHTML={{__html: inline(line.slice(3))}} />); return; }
    if (line.startsWith('### ')) { flushList(); out.push(<h3 key={`h${i}`} className="text-xl font-medium mt-4 mb-2" dangerouslySetInnerHTML={{__html: inline(line.slice(4))}} />); return; }
    if (line.startsWith('- ') || line.startsWith('* ')) { listBuf.push(<li key={`li${i}`} dangerouslySetInnerHTML={{__html: inline(line.slice(2))}} />); return; }
    flushList();
    out.push(<p key={`p${i}`} className="leading-relaxed text-foreground/90 my-3" dangerouslySetInnerHTML={{__html: inline(line)}} />);
  });
  flushList();
  return out;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="blog-post-page">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 md:px-12 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> All posts
        </Link>
        {loading && <p className="text-muted-foreground">Loading…</p>}
        {error && (
          <div className="py-16 text-center">
            <p className="text-muted-foreground">Post not found.</p>
          </div>
        )}
        {post && (
          <article>
            {post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/9] object-cover rounded-2xl mb-8" />
            )}
            <h1 className="text-3xl md:text-4xl font-heading font-medium tracking-tight mb-3" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-8">
              {post.author_name && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{post.author_name}</span>}
              {post.published_at && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(post.published_at).toLocaleDateString()}</span>}
            </div>
            <div className="prose max-w-none">{renderMarkdown(post.body_markdown)}</div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
